import {
    IMeControlConfiguration, AuthenticatedState, AuthProviderConfigType, IConfigurationEvents,
    DisplayModeType, SignInExperienceType, IDP, IPresenceConfig, IPresenceState, MeControlEventType, MeControlEventHandler
} from "@mecontrol/public-api";
import { IValidatedMeControlConfiguration, Scenario, ScenarioStage } from '@mecontrol/common';
import { logResourcesDownloaded, logTelemetryEvent } from './logger';
import { ME, perfNow, hasOwn, createError, setOptions } from './utilities';
import { Promise } from './promise';
import { meImport } from './amd/import';
import { getControllerSetter } from './publicProxy';
import { MainModule } from '.';
import { SyntheticEventTarget } from './utilities/events';

const NO_JS_STYLES = "white-space: nowrap; text-overflow: ellipsis; overflow: hidden; max-width: 160px; display: inline-block; line-height: 48px; font-family: \'Segoe UI\'; font-size: 12.4px; color: rgb(80,80,80); padding: 0 10px;";

let startTime = 0;

/**
 * Initiates the main module of MeControl and returns a promise wrapped around that
 * @param options Validated MeControl options
 */
export function initiateMain(options: IValidatedMeControlConfiguration, callingMethod: string): Promise<void> {
    startTime = perfNow();

    const loadSuccessTelemetry = (success: boolean) => {
        logTelemetryEvent({
            eventType: 'TrackedScenario',
            scenario: Scenario.Load,
            previousAction: ScenarioStage.Start,
            action: ScenarioStage.End,
            success,
            durationMs: getTimeSinceStart(),
            details: callingMethod
        });
    };

    return meImport(ME.Config.main)
        .then((main: MainModule) => main.init(options, getControllerSetter()))
        .then(() => {
            loadSuccessTelemetry(true);
        }, error => {
            // Bubble up error so partners can react, but also render fallback
            // links for partners who don't handle errors. We know suiteshell
            // will render it's fallback in this error case so we shouldn't
            // render ours.
            if (ME.Config.wrapperId !== 'suiteshell') {
                showHeaderNoJs(options);
            }

            loadSuccessTelemetry(false);
            throw error;
        });
}

/**
 * Validates and starts the MeControl. If successful, it will trigger the download of meBoot.js
 * @param options The options to initialize the Me Control
 * @returns True if validation succeeded, false otherwise
 */
export function loadAsync(options: IMeControlConfiguration | undefined): Promise<void> {
    return new Promise((resolve, reject) => {
        // Log downloading resources and use of load function before anything else
        logResourcesDownloaded();
        logTelemetryEvent({ eventType: 'PartnerApiCall', method: 'loadAsync', params: options! });
        resolve(initiateMain(validateConfiguration(options), 'loadAsync'));
    });
}

export function load(options: IMeControlConfiguration | undefined): boolean {
    // Log downloading resources and use of load function before anything else
    logResourcesDownloaded();
    logTelemetryEvent({ eventType: 'PartnerApiCall', method: 'load', params: options! });

    try {
        initiateMain(validateConfiguration(options), 'load');
    }
    catch (e) {
        return false;
    }

    return true;
}

export function getStartTime(): number {
    return startTime;
}

export function getTimeSinceStart(): number {
    return perfNow() - startTime;
}

/** Renders the me control in case of failure downloading the JS files */
export function showHeaderNoJs(options?: IValidatedMeControlConfiguration): void {
    if (!options) {
        throw createError('No options object given before showHeaderNoJs was called');
    }

    const props = getFallbackHeaderProps(options);

    const text = document.createTextNode(props.str || "");

    const a = document.createElement('a');
    a.href = props.href || "#";
    a.style.cssText = NO_JS_STYLES;
    a.onclick = props.onClick;
    a.appendChild(text);

    const wrapper1 = document.createElement('div');
    wrapper1.className = "msame_Header";
    wrapper1.appendChild(a);

    const wrapper2 = document.createElement('div');
    wrapper2.appendChild(wrapper1);

    const container = options.shadowDOMContainerElement || document.getElementById(options.containerId);
    if (container) {
        container.innerHTML = '';
        container.appendChild(wrapper2);
    }
}

interface FallbackHeaderProps {
    href?: string;
    str?: string;
    onClick: ((e: MouseEvent) => void) | null;
}

function getFallbackHeaderProps(options: IValidatedMeControlConfiguration): FallbackHeaderProps {
    const isAuthenticated = options.currentAccount && options.currentAccount.authenticatedState == AuthenticatedState.SignedIn;
    const str = isAuthenticated ? options.primaryUXConfig!.signOutStr : options.primaryUXConfig!.signInStr;

    let href: string | undefined;
    let onClick: (() => void) | null = null;

    // The partner defines the priority of which IDP config is preferred
    const authConfig = options.authProviderConfig;
    switch (authConfig.type) {
        case AuthProviderConfigType.PartnerManaged:
            onClick = isAuthenticated ?
                () => authConfig.signOutFromApp({ currentAccount: options.currentAccount! }) :
                () => authConfig.signIn({ signInType: authConfig.signInExperienceTypes });
            break;
        case AuthProviderConfigType.AdalJsWithMsaFed:
        case AuthProviderConfigType.AdalJsAadOnly:
            onClick = isAuthenticated ?
                () => authConfig.signOutFromApp({ currentAccount: options.currentAccount! }) :
                () => authConfig.signIn({ signInType: SignInExperienceType.Aad });
            break;
        case AuthProviderConfigType.MsalJsAadOnly:
        case AuthProviderConfigType.MsalJsWithMsa:
            // not sure what the default sign in experience should be so I am defaulting to AAD.
            onClick = isAuthenticated ?
                () => authConfig.signOutFromApp({ currentAccount: options.currentAccount! }) :
                () => authConfig.signIn({ signInType: SignInExperienceType.Aad });
            break;
        case AuthProviderConfigType.V1BackCompat:
            const rpData = authConfig.rpData;
            const urlPropName = isAuthenticated ? "signOutUrl" : "signInUrl";
            const aadUrlValue = rpData.aadInfo && rpData.aadInfo[urlPropName];
            const msaUrlValue = rpData.msaInfo && rpData.msaInfo[urlPropName];
            if (rpData.preferredIdp === IDP.AAD && aadUrlValue) {
                href = aadUrlValue;
            }
            else if (rpData.preferredIdp === IDP.MSA && msaUrlValue) {
                href = msaUrlValue;
            }
            else if (aadUrlValue) {
                href = aadUrlValue;
            }
            else if (msaUrlValue) {
                href = msaUrlValue;
            }
            break;
        default:
            const handler = isAuthenticated ? authConfig.appSignOutUrl : authConfig.appSignInUrl;
            href = typeof handler === "function" ? handler({ currentAccount: options.currentAccount! }) : handler;
            break;
    }

    return {
        str,
        href,
        onClick
    };
}

function validatePresenceConfiguration(presenceConfig: IPresenceConfig): boolean {
    return validatePresenceState(presenceConfig.currentPresence) && validatePresenceList(presenceConfig.presenceList);
}

function validatePresenceList(presenceList: IPresenceState[] | undefined): boolean {
    if (presenceList) {
        for (let i = 0; i < presenceList.length; i++) {
            if (!validatePresenceState(presenceList[i])) {
                return false;
            }
        }
        return true;
    }
    return false;
}

function validatePresenceState(state: IPresenceState | undefined): boolean {
    return state ? state.key.length > 0 && state.key != undefined && state.key != null &&
        state.presenceString.length > 0 && state.presenceString != undefined && state.presenceString != null &&
        state.imageUrl.length > 0 && state.imageUrl != undefined && state.imageUrl != null : false;
}

function unwindEventsSpecifiedAsProperties(options: IValidatedMeControlConfiguration): void {
    if (!options || !options.events) {
        return;
    }

    let eventName: keyof IConfigurationEvents;
    for (eventName in options.events) {
        if (!hasOwn(options.events, eventName)) {
            continue;
        }

        let handler = options.events[eventName];
        if (handler) {
            // Must lowercase the event name to appropriately match expected value
            options.syntheticEventTarget.addEventListener(
                eventName.toLowerCase() as MeControlEventType,
                handler as MeControlEventHandler<any>
            );
        }
    }
}

export function validateConfiguration(options: IMeControlConfiguration | undefined): IValidatedMeControlConfiguration {
    // TODO:
    // * Validate idpconfig, if the partner give us a PartnerManagedConfig, the partner doesn't define any other idpconfigs

    if (!options) {
        throw createError(`Options must be an object. Received ${typeof options}.`);
    }

    // Create a copy of the options object to not modify an object we didn't create
    options = { ...options };

    if (!options.authProviderConfig) {
        throw createError(`options.authProviderConfig not correctly defined. Received ${options.authProviderConfig}.`);
    }

    // Presence config validation
    if (options.presenceConfig) {
        validatePresenceConfiguration(options.presenceConfig);
    }

    const uxConfig = options.primaryUXConfig = options.primaryUXConfig || {};
    options.events = options.events || {};

    // TODO: replace with localized fallbacks from ME.Config
    options.primaryUXConfig.signInStr = uxConfig.signInStr || 'Sign in';
    options.primaryUXConfig.signOutStr = uxConfig.signOutStr || 'Sign out';

    // Validate and filter the passed in breakpoints
    const validDisplayModeTypes = [DisplayModeType.Compressed];
    options.primaryUXConfig.displayModes = (
        uxConfig.displayModes &&
        uxConfig.displayModes.filter(dm => validDisplayModeTypes.indexOf(dm.type) != -1)
    ) || [];

    // Clean up and limit the extensibile links to a max of 5 (1 on body, 4 in dropdown)
    if (options.appContextConfig && options.appContextConfig.commands) {
        options.appContextConfig.commands = options.appContextConfig.commands.slice(0, 5);
    }

    // TODO: Try to remove side-effects from this method
    let _options = options as IValidatedMeControlConfiguration;

    // tack the runtime event target onto the _options block to make it universally available
    _options.syntheticEventTarget = new SyntheticEventTarget();

    // unwind event callbacks specified on properties and hook them to our new framework
    unwindEventsSpecifiedAsProperties(_options);
    setOptions(_options);

    return _options;
}
